import { useState, useMemo } from 'react';
import { Search, X} from 'lucide-react';
import Seo from '../components/Seo';
import FAQAccordion from '../components/FAQAccordion';
import { faqData, faqCategories } from '../data/Faqdata';

export default function FAQ() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Filter FAQ by search and category
  const filteredFAQ = useMemo(() => {
    return faqData.filter((item) => {
      const matchesSearch =
        item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory =
        selectedCategory === 'All' || item.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory]);

  const clearSearch = () => {
    setSearchTerm('');
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: filteredFAQ.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
  };

  return (
    <>
      <Seo
        title="Abonnement IPTV FAQ france  - Questions & Answers | SERVICE IPTV"
        description="Find answers to common questions about IPTV in france . Learn about pricing, devices, streaming quality, trials, and more."
        url="https://abonnement-iptvfr.fr/faq"
        image=""
      />

      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            IPTV in france  – Frequently Asked Questions
          </h1>
          <p className="text-xl text-blue-100 mb-8">
            Find clear answers about IPTV subscriptions, pricing, compatible devices, streaming quality, trials and more.
          </p>

          {/* Trust Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🇨🇦</span>
              <span className="text-sm">france -focused</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">📺</span>
              <span className="text-sm">HD & 4K available</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">📱</span>
              <span className="text-sm">All devices work</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">⏰</span>
              <span className="text-sm">24-hour trial</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <button 
           onClick={() =>
                window.open(
                  'https://wa.me/447308508638',
                  '_blank',
                  'noopener,noreferrer'
                )
              }
            className="bg-white text-blue-600 font-bold py-3 px-6 rounded-lg hover:bg-blue-50 transition-colors">
              Start 24-Hour Trial
            </button>
            <button 
            onClick={() =>
                window.open(
                  'https://abonnement-iptvfr.fr/pricing',
                  '_blank',
                  'noopener,noreferrer'
                )
              }
            className="border-2 border-white text-white font-bold py-3 px-6 rounded-lg hover:bg-white hover:text-blue-600 transition-colors">
              View Pricing
            </button>
          </div>
        </div>
      </section>

      {/* Main FAQ Content */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          
          {/* Search Bar */}
          <div className="mb-12">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Search your question..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-12 py-4 border-2 border-slate-300 rounded-lg text-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label="Clear search"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </div>

          {/* Category Filter */}
          <div className="mb-12">
            <h2 className="text-sm font-semibold text-slate-700 mb-4 uppercase tracking-wide">
              Filter by Category
            </h2>
            <div className="flex flex-wrap gap-3">
              {faqCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => {
                    setSelectedCategory(category);
                    setSearchTerm('');
                  }}
                  className={`px-4 py-2 rounded-full font-medium transition-all ${
                    selectedCategory === category
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-slate-700 border-2 border-slate-200 hover:border-blue-500'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Results Count */}
          <div className="mb-8">
            <p className="text-slate-600">
              Showing <span className="font-semibold text-slate-900">{filteredFAQ.length}</span> of{' '}
              <span className="font-semibold text-slate-900">{faqData.length}</span> questions
            </p>
          </div>

          {/* FAQ Accordion */}
          {filteredFAQ.length > 0 ? (
            <FAQAccordion items={filteredFAQ} />
          ) : (
            <div className="text-center py-12">
              <p className="text-slate-600 text-lg mb-4">No questions found matching your search.</p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('All');
                }}
                className="text-blue-600 font-semibold hover:text-blue-700"
              >
                Clear filters and try again
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Still have questions?
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Our 24/7 customer support team is ready to help. Available in English and French.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button onClick={() =>
                window.open(
                  'https://abonnement-iptvfr.fr/contact',
                  '_blank',
                  'noopener,noreferrer'
                )
              }
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition-colors">
              Contact Support
            </button>
            <button onClick={() =>
                window.open(
                  'https://wa.me/447308508638',
                  '_blank',
                  'noopener,noreferrer'
                )
              } className="border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-slate-900 font-bold py-3 px-8 rounded-lg transition-colors">
              Start Free Trial
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
